import React, { useState } from "react";
import {
  AppBar,
  Toolbar,
  Button,
  Box,
  Drawer,
  IconButton,
  useMediaQuery,
  Modal,
  TextField,
  Typography,
} from "@mui/material";
import { styled } from "@mui/system";
import { useTheme } from "@mui/material/styles";
import MenuIcon from "@mui/icons-material/Menu";
import Swal from "sweetalert2";
import logo from "../../img/logo.png";

const Navbar = styled(AppBar)({
  backgroundColor: "#1E1E2F",
  padding: "10px 20px",
});

const Logo = styled("img")({
  height: "50px",
});

const ButtonContainer = styled(Box)({
  display: "flex",
  gap: "10px",
});

const CustomButton = styled(Button)({
  color: "white",
  backgroundColor: "#1D8DCD",
  padding: "8px 16px",
  borderRadius: "8px",
  textTransform: "none",
  fontWeight: "bold",
  "&:hover": {
    backgroundColor: "#2C3A9B",
  },
});

const Nav = ({ onRegisterClick, onLoginClick, onVartmanNiryanClick }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [openLoginModal, setOpenLoginModal] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [activeButton, setActiveButton] = useState("Ranking Test"); // Default active button

  const toggleDrawer = (open) => () => {
    setDrawerOpen(open);
  };

  const handleLoginOpen = () => {
    setActiveButton("Login");
    setOpenLoginModal(true);
  };

  const handleLoginClose = () => setOpenLoginModal(false);

  const handleLogin = async () => {
    try {
      const params = new URLSearchParams();
      params.append("email", email);
      params.append("password", password);

      const response = await fetch(`https://vartmannirnay.com:35443/login?${params.toString()}`, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
      });

      const data = await response.text(); // API returns text response
      console.log("Response Status:", response.status, "Data:", data);

      if (response.ok) {
        setOpenLoginModal(false); // 🔹 CLOSE MODAL IMMEDIATELY FOR SUCCESS

        Swal.fire({
          icon: "success",
          title: "Login Successful",
          text: "Welcome back!",
          timer: 2000,
          showConfirmButton: false,
        }).then(() => {
          window.location.href = "/home"; // 🔹 REDIRECT AFTER ALERT CLOSES
        });
      } else {
        Swal.fire({
          icon: "error",
          title: "Login Failed",
          text: data || "Invalid credentials",
        }).then(() => {
          setOpenLoginModal(true); // 🔹 KEEP MODAL OPEN AFTER ERROR ALERT
        });
      }
    } catch (error) {
      console.error("Login Error:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Something went wrong. Please try again later.",
      }).then(() => {
        setOpenLoginModal(true); // 🔹 KEEP MODAL OPEN AFTER ERROR ALERT
      });
    }
  };

  const handleButtonClick = (buttonName, callback) => {
    setActiveButton(buttonName);
    callback();
  };

  return (
    <Navbar position="static">
      <Toolbar>
        <Logo src={logo} alt="Logo" />
        <Box sx={{ flexGrow: 1 }} />
        {isMobile ? (
          <>
            <IconButton color="inherit" onClick={toggleDrawer(true)}>
              <MenuIcon />
            </IconButton>
            <Drawer anchor="right" open={drawerOpen} onClose={toggleDrawer(false)}>
              <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: "10px" }}>
                <CustomButton
                  onClick={() => handleButtonClick("Ranking Test", onRegisterClick)}
                  sx={{
                    backgroundColor: activeButton === "Ranking Test" ? "#EB1F26" : "#1D8DCD",
                  }}
                >
                  Ranking Test
                </CustomButton>
                <CustomButton
                  onClick={() => handleButtonClick("Vartman Niryan", onVartmanNiryanClick)}
                  sx={{
                    backgroundColor: activeButton === "Vartman Niryan" ? "#EB1F26" : "#1D8DCD",
                  }}
                >
                  Vartman Niryan
                </CustomButton>
                <CustomButton
                  onClick={() => handleButtonClick("Register", onLoginClick)}
                  sx={{
                    backgroundColor: activeButton === "Register" ? "#EB1F26" : "#1D8DCD",
                  }}
                >
                  Register
                </CustomButton>
                <CustomButton
                  onClick={() => handleButtonClick("Login", handleLoginOpen)}
                  sx={{
                    backgroundColor: activeButton === "Login" ? "#EB1F26" : "#1D8DCD",
                  }}
                >
                  Login
                </CustomButton>
              </Box>
            </Drawer>
          </>
        ) : (
          <ButtonContainer>
            <CustomButton
              onClick={() => handleButtonClick("Ranking Test", onRegisterClick)}
              sx={{
                backgroundColor: activeButton === "Ranking Test" ? "#EB1F26" : "#1D8DCD",
              }}
            >
              Ranking Test
            </CustomButton>
            <CustomButton
              onClick={() => handleButtonClick("Vartman Niryan", onVartmanNiryanClick)}
              sx={{
                backgroundColor: activeButton === "Vartman Niryan" ? "#EB1F26" : "#1D8DCD",
              }}
            >
              Vartman Niryan
            </CustomButton>
            <CustomButton
              onClick={() => handleButtonClick("Register", onLoginClick)}
              sx={{
                backgroundColor: activeButton === "Register" ? "#EB1F26" : "#1D8DCD",
              }}
            >
              Register
            </CustomButton>
            <CustomButton
              onClick={() => handleButtonClick("Login", handleLoginOpen)}
              sx={{
                backgroundColor: activeButton === "Login" ? "#EB1F26" : "#1D8DCD",
              }}
            >
              Login
            </CustomButton>
          </ButtonContainer>
        )}
      </Toolbar>

      {/* Login Modal */}
      <Modal open={openLoginModal} onClose={handleLoginClose}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 300,
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 4,
            borderRadius: "10px",
          }}
        >
          <Typography variant="h6" sx={{ mb: 2 }}>
            Login
          </Typography>
          <TextField
            fullWidth
            label="Email"
            sx={{ mb: 2 }}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <TextField
            fullWidth
            label="Password"
            type="password"
            sx={{ mb: 2 }}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          <CustomButton onClick={handleLogin} fullWidth>
            Submit
          </CustomButton>
        </Box>
      </Modal>
    </Navbar>
  );
};

export default Nav;
